import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Navigation from "../components/Navigation";
import axios from "axios";
import {
  Search, TrendingUp, Users, Hash, Zap, Heart, Trophy,
  Sun, Leaf, Target, ChevronRight, Star, MessageCircle
} from "lucide-react";

const categoryIcons = {
  motivation: Zap,
  family: Heart,
  success: Trophy,
  gratitude: Sun,
  wellness: Leaf,
  goals: Target,
};

export default function Explore() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchExplore();
  }, []);

  const fetchExplore = async () => {
    try {
      const response = await axios.get(`${API}/explore`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch explore:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await axios.get(`${API}/explore/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-12 w-full bg-amber-100 rounded-xl"></div>
            <div className="grid grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 bg-white rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pb-24">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts, people, or #hashtags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-24 py-4 rounded-2xl bg-white border border-gray-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-lg"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-20 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
            <button
              type="submit"
              disabled={searching}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
            >
              {searching ? "..." : "Search"}
            </button>
          </div>
        </form>

        {/* Search Results */}
        {searchResults ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Search Results</h2>
              <button onClick={clearSearch} className="text-amber-600 hover:underline">
                Clear
              </button>
            </div>

            {/* Users */}
            {searchResults.users?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" /> People
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {searchResults.users.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => navigate(`/profile/${user.id}`)}
                      className="bg-white p-4 rounded-xl flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                        {user.profile_photo ? (
                          <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-amber-600 font-bold">{user.name?.[0]}</span>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.tier} • {user.bio?.substring(0, 50)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Posts */}
            {searchResults.posts?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Posts
                </h3>
                <div className="space-y-3">
                  {searchResults.posts.map((post) => (
                    <div key={post.id} className="bg-white p-4 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                          {post.user_photo ? (
                            <img src={post.user_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-amber-600 text-sm font-bold">{post.user_name?.[0]}</span>
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{post.user_name}</span>
                      </div>
                      <p className="text-gray-700">{post.content}</p>
                      <div className="flex gap-4 mt-2 text-sm text-gray-500">
                        <span>❤️ {post.hearts || 0}</span>
                        <span>⭐ {post.stars || 0}</span>
                        <span>🤗 {post.hugs || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {searchResults.posts?.length === 0 && searchResults.users?.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No results found for "{searchQuery}"
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Categories */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Hash className="w-5 h-5 text-amber-600" />
                Browse Categories
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {data?.categories?.map((category) => {
                  const Icon = categoryIcons[category.id] || Zap;
                  return (
                    <button
                      key={category.id}
                      onClick={() => navigate(`/explore/${category.id}`)}
                      className="bg-white p-4 rounded-xl hover:shadow-md transition-all hover:-translate-y-1 text-center"
                      style={{ borderLeft: `4px solid ${category.color}` }}
                    >
                      <Icon className="w-8 h-8 mx-auto mb-2" style={{ color: category.color }} />
                      <span className="font-medium text-gray-700">{category.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Trending Hashtags */}
            {data?.trending_hashtags?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                  Trending Hashtags
                </h2>
                <div className="flex flex-wrap gap-2">
                  {data.trending_hashtags.map((tag, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchQuery(tag.tag)}
                      className="px-4 py-2 bg-white rounded-full border border-gray-200 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                    >
                      <span className="text-amber-600 font-medium">{tag.tag}</span>
                      <span className="text-gray-400 text-sm ml-2">{tag.count} posts</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Users */}
            {data?.suggested_users?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  People You May Know
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.suggested_users.slice(0, 6).map((suggestedUser) => (
                    <div
                      key={suggestedUser.id}
                      className="bg-white p-4 rounded-xl flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/profile/${suggestedUser.id}`)}
                    >
                      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {suggestedUser.profile_photo ? (
                          <img src={suggestedUser.profile_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-amber-600 text-xl font-bold">{suggestedUser.name?.[0]}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{suggestedUser.name}</div>
                        <div className="text-sm text-gray-500">{suggestedUser.tier}</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trending Posts */}
            {data?.trending_posts?.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                  Trending Posts
                </h2>
                <div className="space-y-4">
                  {data.trending_posts.map((post) => (
                    <div key={post.id} className="bg-white p-5 rounded-xl hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                          {post.user_photo ? (
                            <img src={post.user_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-amber-600 font-bold">{post.user_name?.[0]}</span>
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{post.user_name}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(post.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="ml-auto flex items-center gap-1 bg-amber-100 px-3 py-1 rounded-full">
                          <TrendingUp className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-700">
                            {post.total_reactions} reactions
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-3">{post.content}</p>
                      <div className="flex gap-6 text-sm">
                        <span className="flex items-center gap-1 text-red-500">
                          <Heart className="w-4 h-4" /> {post.hearts || 0}
                        </span>
                        <span className="flex items-center gap-1 text-yellow-500">
                          <Star className="w-4 h-4" /> {post.stars || 0}
                        </span>
                        <span className="flex items-center gap-1 text-pink-500">
                          🤗 {post.hugs || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
